import { TIER_DISCOUNT_TYPES, formatTierDiscountValue } from './pricingTierConfig';

export { TIER_DISCOUNT_TYPES };

export const CART_DISCOUNT_TIER_MAX_THRESHOLDS = 10;

const newLocalId = () => `cart-tier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const createEmptyCartThreshold = (overrides = {}) => ({
    localId: newLocalId(),
    min_subtotal: '',
    discount_value: '',
    ...overrides,
});

export const thresholdsToCartRows = (thresholds = []) => {
    const sorted = [...thresholds].sort((a, b) => a.min_subtotal - b.min_subtotal);
    if (sorted.length === 0) {
        return [createEmptyCartThreshold()];
    }
    return sorted.map((t) => ({
        localId: newLocalId(),
        min_subtotal: String(t.min_subtotal),
        discount_value: String(t.discount_value),
    }));
};

export const cartRowsToThresholds = (rows) => {
    const parsed = rows
        .map((row) => ({
            min_subtotal: Number(row.min_subtotal),
            discount_value: Number(row.discount_value),
        }))
        .filter((row) => Number.isFinite(row.min_subtotal) && row.min_subtotal > 0);

    parsed.sort((a, b) => a.min_subtotal - b.min_subtotal);

    const seen = new Set();
    return parsed.filter((row) => {
        if (seen.has(row.min_subtotal)) return false;
        seen.add(row.min_subtotal);
        return true;
    });
};

export const sortCartThresholdRows = (rows) =>
    [...rows].sort((a, b) => Number(a.min_subtotal) - Number(b.min_subtotal));

export const parseCartDiscountTierResponse = (raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const nested = raw.response !== undefined ? raw.response : raw;
    if (nested == null) return null;
    if (typeof nested === 'object' && nested.id) return nested;
    return null;
};

export const buildCartDiscountTierPayload = ({
    name,
    discount_type,
    thresholds,
    active,
    starts_at,
    ends_at,
    useSchedule,
}) => {
    const payload = {
        name: name.trim(),
        discount_type,
        thresholds,
        active: Boolean(active),
    };
    if (useSchedule) {
        payload.starts_at = starts_at;
        payload.ends_at = ends_at;
    } else {
        payload.starts_at = null;
        payload.ends_at = null;
    }
    return payload;
};

export const formatCartSubtotalLabel = (value, currency) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';
    if (currency) return `${amount.toLocaleString('es')} ${currency}`;
    return amount.toLocaleString('es');
};

/** Shorter label for table chips (e.g. 500k COP instead of 500.000 COP). */
export const formatCartSubtotalChipMin = (value, currency) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';

    let short = '';
    if (amount >= 1_000_000) {
        const millions = amount / 1_000_000;
        short = Number.isInteger(millions) ? `${millions}M` : `${millions.toFixed(1)}M`;
    } else if (amount >= 1_000) {
        const thousands = amount / 1_000;
        short = Number.isInteger(thousands) ? `${thousands}k` : `${thousands.toFixed(1)}k`;
    } else {
        short = amount.toLocaleString('es');
    }

    return currency ? `${short} ${currency}` : short;
};

export const formatCartTierSubtotalRange = (sortedRows, index, currency) => {
    const min = Number(sortedRows[index]?.min_subtotal);
    if (!Number.isFinite(min) || min <= 0) return '';

    const nextMin = Number(sortedRows[index + 1]?.min_subtotal);
    if (Number.isFinite(nextMin) && nextMin > min) {
        return `${formatCartSubtotalLabel(min, currency)} – ${formatCartSubtotalLabel(
            nextMin - 0.01,
            currency
        )}`;
    }
    return `${formatCartSubtotalLabel(min, currency)} o más`;
};

export const getCartThresholdDisplayItems = (rule, currency, options = {}) => {
    const { compactMin = false } = options;
    const thresholds = Array.isArray(rule?.thresholds) ? rule.thresholds : [];
    const sorted = [...thresholds].sort((a, b) => a.min_subtotal - b.min_subtotal);

    return sorted.map((threshold) => {
        const value = formatTierDiscountValue(
            rule.discount_type,
            threshold.discount_value,
            currency
        );
        const minLabel = compactMin
            ? formatCartSubtotalChipMin(threshold.min_subtotal, currency)
            : formatCartSubtotalLabel(threshold.min_subtotal, currency);
        return {
            key: threshold.min_subtotal,
            chipLabel: `${minLabel}+ → ${value}`,
        };
    });
};

export const formatCartDiscountTierSummary = (rule, currency) => {
    const items = getCartThresholdDisplayItems(rule, currency);
    if (items.length === 0) return '—';
    return items.map((item) => item.chipLabel).join(' · ');
};

export const mapCartDiscountTierToListRow = (rule) => ({
    ...rule,
    ruleKind: 'cart_subtotal',
});

export const buildCartDiscountTierTogglePayload = (rule) =>
    buildCartDiscountTierPayload({
        name: rule.name,
        discount_type: rule.discount_type,
        thresholds: rule.thresholds || [],
        active: !rule.active,
        useSchedule: Boolean(rule.starts_at || rule.ends_at),
        starts_at: rule.starts_at ?? null,
        ends_at: rule.ends_at ?? null,
    });

export const getCartMinSubtotalError = (sortedRows, index) => {
    const raw = sortedRows[index]?.min_subtotal;
    if (raw === '' || raw == null) {
        return 'Ingresa un monto mínimo';
    }

    const min = Number(raw);
    if (!Number.isFinite(min) || min <= 0) {
        return 'Ingresa un monto mínimo mayor a 0';
    }
    if (index > 0) {
        const prev = Number(sortedRows[index - 1].min_subtotal);
        if (Number.isFinite(prev) && min <= prev) {
            return 'El monto debe ser mayor al nivel anterior';
        }
    }
    return null;
};

export const getCartDiscountValueError = (sortedRows, index, discountType = 'percentage') => {
    const raw = sortedRows[index]?.discount_value;
    if (raw === '' || raw == null) {
        return 'Ingresa un valor de descuento';
    }

    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) {
        return 'Ingresa un valor de descuento válido';
    }
    if (discountType === 'percentage' && value > 100) {
        return 'El porcentaje no puede ser mayor a 100';
    }
    if (index > 0) {
        const prev = Number(sortedRows[index - 1].discount_value);
        if (Number.isFinite(prev) && value <= prev) {
            return 'El descuento debe ser mayor al nivel anterior';
        }
    }
    return null;
};

export const validateCartDiscountTierForm = (rows, discountType = 'percentage') => {
    const sorted = sortCartThresholdRows(rows);

    for (let index = 0; index < sorted.length; index += 1) {
        const minError = getCartMinSubtotalError(sorted, index);
        if (minError) {
            return { valid: false, message: minError };
        }
        const discountError = getCartDiscountValueError(sorted, index, discountType);
        if (discountError) {
            return { valid: false, message: discountError };
        }
    }

    return { valid: true };
};
